import { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  ToggleLeft,
  ToggleRight,
  Inbox,
  ScrollText,
  Sparkles,
  X,
  Check,
  Timer,
} from "lucide-react";
import { toast } from "sonner";
import {
  getRules,
  createRule,
  updateRule,
  deleteRule,
  getInboxOutros,
} from "@/lib/ocorenApi";
import { CATEGORIAS, getCategoria } from "@/lib/categorias";
import { RegrasAtraso } from "@/components/RegrasAtraso";

const CategoriaBadge = ({ catKey }) => {
  const c = getCategoria(catKey);
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${c.bg} ${c.text} ${c.border}`}
    >
      <Icon size={11} />
      {c.label}
    </span>
  );
};

const RuleForm = ({ initial, onCancel, onSaved }) => {
  const isEdit = !!(initial && initial.id);
  const [pattern, setPattern] = useState(initial?.pattern || "");
  const [target, setTarget] = useState(initial?.target_category || "problema");
  const [priority, setPriority] = useState(initial?.priority ?? 50);
  const [description, setDescription] = useState(initial?.description || "");
  const [active, setActive] = useState(initial?.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!pattern.trim()) {
      toast.error("Padrão (regex) é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pattern: pattern.trim(),
        target_category: target,
        priority: Number(priority) || 50,
        description: description.trim(),
        active,
      };
      if (isEdit) {
        await updateRule(initial.id, payload);
        toast.success("Regra atualizada");
      } else {
        await createRule(payload);
        toast.success("Regra criada");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="rule-form"
      className="bg-white border border-[#09090B] rounded-md p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-[#09090B]">
          {isEdit ? "Editar regra" : "Nova regra"}
        </h3>
        <button
          onClick={onCancel}
          className="text-[#52525B] hover:text-[#09090B]"
          data-testid="rule-form-close"
        >
          <X size={16} />
        </button>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
          Padrão (regex)
        </label>
        <input
          data-testid="rule-form-pattern"
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          placeholder="MARCIO ENTREGOU|NOTAS FISCAIS VENCIDAS"
          className="w-full px-3 py-2 text-sm font-mono-data border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
        />
        <div className="text-[10px] text-[#52525B] mt-1">
          Use `|` para múltiplos termos. Case-insensitive. Ex: <code>AGENDAMENTO|AGENDAD</code>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
            Categoria-alvo
          </label>
          <select
            data-testid="rule-form-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
          >
            {CATEGORIAS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
            Prioridade
          </label>
          <input
            data-testid="rule-form-priority"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            min="0"
            max="999"
            className="w-full px-3 py-2 text-sm font-mono-data border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
          />
          <div className="text-[10px] text-[#52525B] mt-1">
            Maior = avaliada primeiro
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5">
          Descrição (opcional)
        </label>
        <input
          data-testid="rule-form-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex: Motorista Marcio entregou na rota errada"
          className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          data-testid="rule-form-active"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="w-4 h-4"
        />
        <span className="text-sm text-[#09090B]">Regra ativa</span>
      </label>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E4E4E7]">
        <button
          onClick={onCancel}
          className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-4 py-2 text-sm font-medium rounded-md"
        >
          Cancelar
        </button>
        <button
          data-testid="rule-form-save"
          onClick={save}
          disabled={saving}
          className="bg-[#09090B] text-white hover:bg-[#27272A] disabled:opacity-60 px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2"
        >
          <Check size={14} />
          {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
};

export default function Regras() {
  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([getRules(), getInboxOutros(50)]);
      setRules(r.items || []);
      setInbox(i.items || []);
    } catch {
      toast.error("Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreate = (descricao) => {
    setEditing(null);
    if (descricao) {
      // Cria a partir do inbox: prepara padrão escapando caracteres especiais
      const escaped = descricao
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .toUpperCase();
      setPrefill({
        pattern: escaped,
        target_category: "problema",
        priority: 60,
        description: `A partir do Inbox: "${descricao.slice(0, 50)}…"`,
      });
    } else {
      setPrefill(null);
    }
    setCreating(true);
  };

  const onSaved = async () => {
    setCreating(false);
    setEditing(null);
    setPrefill(null);
    await refresh();
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Remover regra "${rule.pattern}"?`)) return;
    try {
      await deleteRule(rule.id);
      toast.success("Regra removida");
      refresh();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  const onToggle = async (rule) => {
    try {
      await updateRule(rule.id, { active: !rule.active });
      refresh();
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  return (
    <div data-testid="page-regras" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Configuração
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Regras de Classificação
          </h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            Adicione regras customizadas (regex) para classificar automaticamente
            descrições que estão caindo como &quot;Outros&quot;. Cada regra é aplicada em
            ordem de prioridade decrescente.
          </p>
        </div>
        <button
          data-testid="rule-create-btn"
          onClick={() => onCreate()}
          className="bg-[#09090B] text-white hover:bg-[#27272A] px-5 py-2.5 text-sm font-semibold rounded-md flex items-center gap-2"
        >
          <Plus size={15} />
          Nova regra
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E4E4E7] mb-6">
        <button
          data-testid="tab-rules"
          onClick={() => setTab("rules")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            tab === "rules"
              ? "border-[#09090B] text-[#09090B]"
              : "border-transparent text-[#52525B] hover:text-[#09090B]"
          }`}
        >
          <ScrollText size={14} />
          Regras Configuradas ({rules.length})
        </button>
        <button
          data-testid="tab-inbox"
          onClick={() => setTab("inbox")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            tab === "inbox"
              ? "border-[#09090B] text-[#09090B]"
              : "border-transparent text-[#52525B] hover:text-[#09090B]"
          }`}
        >
          <Inbox size={14} />
          Inbox de Outros ({inbox.length})
        </button>
        <button
          data-testid="tab-atraso"
          onClick={() => setTab("atraso")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            tab === "atraso"
              ? "border-[#09090B] text-[#09090B]"
              : "border-transparent text-[#52525B] hover:text-[#09090B]"
          }`}
        >
          <Timer size={14} />
          Regras de Atraso (SLA)
        </button>
      </div>

      {(creating || editing) && (
        <div className="mb-6">
          <RuleForm
            initial={editing || prefill}
            onCancel={() => {
              setCreating(false);
              setEditing(null);
              setPrefill(null);
            }}
            onSaved={onSaved}
            key={editing?.id || prefill?.pattern || "new"}
          />
        </div>
      )}

      {/* Tab content */}
      {tab === "rules" && (
        <div
          data-testid="rules-table-wrapper"
          className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden"
        >
          <table className="data-table w-full text-sm" data-testid="rules-table">
            <thead>
              <tr>
                <th>Padrão (regex)</th>
                <th>Alvo</th>
                <th className="text-right">Prioridade</th>
                <th>Descrição</th>
                <th className="text-center">Ativa</th>
                <th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-[#52525B] py-10">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-[#52525B] py-10">
                    Nenhuma regra criada ainda. Use a aba &quot;Inbox de Outros&quot; para
                    descobrir descrições não classificadas.
                  </td>
                </tr>
              )}
              {!loading &&
                rules.map((r, idx) => (
                  <tr key={r.id} data-testid={`rule-row-${idx}`}>
                    <td>
                      <code className="text-xs font-mono-data bg-[#FAFAFA] px-2 py-0.5 rounded-sm border border-[#E4E4E7]">
                        {r.pattern}
                      </code>
                    </td>
                    <td>
                      <CategoriaBadge catKey={r.target_category} />
                    </td>
                    <td className="text-right font-mono-data text-xs">
                      {r.priority}
                    </td>
                    <td className="text-xs text-[#52525B] max-w-md truncate">
                      {r.description || "—"}
                    </td>
                    <td className="text-center">
                      <button
                        data-testid={`rule-toggle-${idx}`}
                        onClick={() => onToggle(r)}
                        className="text-[#09090B] hover:opacity-70"
                      >
                        {r.active ? (
                          <ToggleRight size={20} className="text-emerald-600" />
                        ) : (
                          <ToggleLeft size={20} className="text-[#A1A1AA]" />
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`rule-edit-${idx}`}
                          onClick={() => {
                            setCreating(false);
                            setPrefill(null);
                            setEditing(r);
                          }}
                          className="p-1.5 hover:bg-[#FAFAFA] rounded-sm text-[#52525B] hover:text-[#09090B]"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          data-testid={`rule-delete-${idx}`}
                          onClick={() => onDelete(r)}
                          className="p-1.5 hover:bg-[#FAFAFA] rounded-sm text-[#52525B] hover:text-[#F43F5E]"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "inbox" && (
        <div
          data-testid="inbox-wrapper"
          className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-[#E4E4E7] bg-[#FAFAFA] text-xs text-[#52525B]">
            Descrições atualmente classificadas como{" "}
            <span className="font-semibold text-[#09090B]">Outros</span>,
            agrupadas por ocorrência. Clique em &quot;Criar regra&quot; para classificá-las
            automaticamente.
          </div>
          <table className="data-table w-full text-sm" data-testid="inbox-table">
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="text-right">Total</th>
                <th>Código</th>
                <th>Exemplo NF</th>
                <th className="w-32 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center text-[#52525B] py-10">
                    Carregando…
                  </td>
                </tr>
              )}
              {!loading && inbox.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-[#52525B] py-10">
                    🎉 Inbox limpo — todas descrições estão classificadas.
                  </td>
                </tr>
              )}
              {!loading &&
                inbox.map((it, idx) => (
                  <tr key={`${it.descricao}-${idx}`} data-testid={`inbox-row-${idx}`}>
                    <td>
                      <div className="text-[#09090B] max-w-2xl truncate">
                        {it.descricao || (
                          <em className="text-[#A1A1AA]">(vazio)</em>
                        )}
                      </div>
                    </td>
                    <td className="text-right font-mono-data font-semibold text-[#09090B]">
                      {it.total.toLocaleString("pt-BR")}
                    </td>
                    <td className="font-mono-data text-xs text-[#52525B]">
                      {it.codigo_ocorrencia || "—"}
                    </td>
                    <td className="font-mono-data text-xs text-[#52525B]">
                      {it.exemplo_nf || "—"}
                    </td>
                    <td className="text-right">
                      <button
                        data-testid={`inbox-classify-${idx}`}
                        onClick={() => onCreate(it.descricao || "")}
                        disabled={!it.descricao}
                        className="bg-[#09090B] text-white hover:bg-[#27272A] disabled:opacity-30 px-3 py-1 text-xs font-semibold rounded-sm inline-flex items-center gap-1"
                      >
                        <Sparkles size={11} />
                        Criar regra
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "atraso" && <RegrasAtraso />}
    </div>
  );
}
